import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Loader2, Calendar, Users, ShoppingCart, Tag, Info, DollarSign, Percent } from 'lucide-react';
import { DiscountAPI, Discount, DiscountCreateRequest, DiscountUpdateRequest } from '../../lib/api';
import { toast } from '../ui/use-toast';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';
import { CurrencyInput } from '../ui/currency-input';
import { NumberInput } from '../ui/number-input';

interface DiscountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  discount?: Discount | null;
}

interface FormData {
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'shipping';
  value: number;
  minCartValue: number;
  maxDiscountAmount: number;
  validFrom: string;
  validTo: string;
  usageLimit: number;
  perUserLimit: number;
  appliesTo: 'all' | 'categories' | 'products';
  firstTimeUsers: boolean;
  isActive: boolean;
}

export default function DiscountFormModal({
  isOpen,
  onClose,
  onSuccess,
  discount
}: DiscountFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    code: '',
    description: '',
    discountType: 'percentage',
    value: 0,
    minCartValue: 0,
    maxDiscountAmount: 0,
    validFrom: '',
    validTo: '',
    usageLimit: 0,
    perUserLimit: 1,
    appliesTo: 'all',
    firstTimeUsers: false,
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when discount prop changes
  useEffect(() => {
    if (discount) {
      setFormData({
        code: discount.code,
        description: discount.description,
        discountType: discount.discountType,
        value: discount.value,
        minCartValue: discount.minCartValue,
        maxDiscountAmount: discount.maxDiscountAmount || 0,
        validFrom: discount.validFrom.split('T')[0],
        validTo: discount.validTo.split('T')[0],
        usageLimit: discount.usageLimit || 0,
        perUserLimit: discount.perUserLimit,
        appliesTo: discount.appliesTo,
        firstTimeUsers: discount.firstTimeUsers,
        isActive: discount.isActive
      });
    } else {
      // Reset form for new discount
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const nextMonthStr = nextMonth.toISOString().split('T')[0];

      setFormData({
        code: '',
        description: '',
        discountType: 'percentage',
        value: 0,
        minCartValue: 0,
        maxDiscountAmount: 0,
        validFrom: today,
        validTo: nextMonthStr,
        usageLimit: 0,
        perUserLimit: 1,
        appliesTo: 'all',
        firstTimeUsers: false,
        isActive: true
      });
    }
    setErrors({});
  }, [discount, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Discount code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Code must be at least 3 characters';
    } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters and numbers';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Value validation (not required for shipping type)
    if (formData.discountType !== 'shipping') {
      if (!formData.value || formData.value <= 0) {
        newErrors.value = 'Discount value is required';
      } else if (formData.discountType === 'percentage' && formData.value > 100) {
        newErrors.value = 'Percentage cannot exceed 100%';
      }
    }

    // Min cart value validation
    if (formData.minCartValue < 0) {
      newErrors.minCartValue = 'Min cart value cannot be negative';
    }

    // Max discount amount validation (for percentage discounts)
    if (formData.discountType === 'percentage' && formData.maxDiscountAmount && formData.maxDiscountAmount <= 0) {
      newErrors.maxDiscountAmount = 'Max discount amount must be positive';
    }

    // Date validation
    if (!formData.validFrom) {
      newErrors.validFrom = 'Valid from date is required';
    }
    if (!formData.validTo) {
      newErrors.validTo = 'Valid to date is required';
    }
    if (formData.validFrom && formData.validTo && formData.validFrom >= formData.validTo) {
      newErrors.validTo = 'End date must be after start date';
    }

    // Usage limit validation
    if (formData.usageLimit && formData.usageLimit <= 0) {
      newErrors.usageLimit = 'Usage limit must be positive';
    }

    // Per user limit validation
    if (!formData.perUserLimit || formData.perUserLimit <= 0) {
      newErrors.perUserLimit = 'Per user limit must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare request data
      const requestData: DiscountCreateRequest | DiscountUpdateRequest = {
        code: formData.code.toUpperCase(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        value: formData.discountType === 'shipping' ? 0 : formData.value,
        minCartValue: formData.minCartValue,
        validFrom: new Date(formData.validFrom).toISOString(),
        validTo: new Date(formData.validTo + 'T23:59:59.999Z').toISOString(),
        perUserLimit: formData.perUserLimit,
        appliesTo: formData.appliesTo,
        firstTimeUsers: formData.firstTimeUsers
      };

      // Add optional fields
      if (formData.maxDiscountAmount > 0) {
        requestData.maxDiscountAmount = formData.maxDiscountAmount;
      }
      if (formData.usageLimit > 0) {
        requestData.usageLimit = formData.usageLimit;
      }

      // Add isActive for updates
      if (discount) {
        (requestData as DiscountUpdateRequest).isActive = formData.isActive;
      }

      let response;
      if (discount) {
        response = await DiscountAPI.update(discount._id, requestData as DiscountUpdateRequest);
      } else {
        response = await DiscountAPI.create(requestData as DiscountCreateRequest);
      }

      if (response.success) {
        toast({
          title: "Success",
          description: discount ? "Discount updated successfully" : "Discount created successfully"
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to save discount",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error saving discount:', error);
      toast({
        title: "Error",
        description: "Failed to save discount",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // If discount type changes to shipping, clear the value field
      if (field === 'discountType' && value === 'shipping') {
        newData.value = 0;
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={onClose}>
      <EnhancedDialogContent size="2xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle>
            {discount ? 'Edit Discount Code' : 'Create New Discount Code'}
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            {discount ? 'Update the discount code settings' : 'Create a new discount code for your customers'}
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            {/* Basic Information */}
            <FormSection
              title="Basic Information"
              icon={<Tag className="h-5 w-5" />}
              description="Set up the basic discount code details"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Discount Code"
                  required
                  error={errors.code}
                >
                  <Input
                    placeholder="e.g., WELCOME20"
                    value={formData.code}
                    onChange={(e) => updateFormData('code', e.target.value.toUpperCase())}
                    className={errors.code ? 'border-destructive' : ''}
                  />
                </FormField>

                <FormField
                  label="Discount Type"
                  required
                  error={errors.discountType}
                >
                  <Select
                    value={formData.discountType}
                    onValueChange={(value) => updateFormData('discountType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          Percentage (%)
                        </div>
                      </SelectItem>
                      <SelectItem value="fixed">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">₹</span>
                          Fixed Amount (₹)
                        </div>
                      </SelectItem>
                      <SelectItem value="shipping">
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Free Shipping
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </FormGrid>

              <FormField
                label="Description"
                required
                error={errors.description}
                hint="Describe what this discount is for"
              >
                <Textarea
                  placeholder="Describe what this discount is for..."
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  className={errors.description ? 'border-destructive' : ''}
                  rows={3}
                />
              </FormField>
            </FormSection>

            {/* Discount Value */}
            <FormSection
              title="Discount Value"
              icon={<span className="text-lg">₹</span>}
              description="Configure the discount amount and constraints"
            >
              <FormGrid columns={2}>
                <FormField
                  label={
                    formData.discountType === 'percentage' ? 'Percentage (%)' :
                      formData.discountType === 'fixed' ? 'Amount (₹)' :
                        'Free Shipping'
                  }
                  required={formData.discountType !== 'shipping'}
                  error={errors.value}
                >
                  {formData.discountType === 'shipping' ? (
                    <div className="h-10 flex items-center px-3 border rounded-md bg-muted">
                      <Badge variant="outline">Free Shipping Applied</Badge>
                    </div>
                  ) : formData.discountType === 'percentage' ? (
                    <NumberInput
                      value={formData.value}
                      onChange={(value) => updateFormData('value', value)}
                      placeholder="e.g., 20"
                      maxValue={100}
                      minValue={0}
                      unit="%"
                      allowDecimals={true}
                      error={errors.value}
                    />
                  ) : (
                    <CurrencyInput
                      value={formData.value}
                      onChange={(value) => updateFormData('value', value)}
                      placeholder="500.00"
                      minValue={0}
                      error={errors.value}
                    />
                  )}
                </FormField>

                <FormField
                  label="Minimum Cart Value"
                  error={errors.minCartValue}
                  hint="Minimum order amount to apply this discount"
                >
                  <CurrencyInput
                    value={formData.minCartValue}
                    onChange={(value) => updateFormData('minCartValue', value)}
                    placeholder="0.00"
                    minValue={0}
                    error={errors.minCartValue}
                  />
                </FormField>
              </FormGrid>

              {formData.discountType === 'percentage' && (
                <FormField
                  label="Maximum Discount Amount"
                  error={errors.maxDiscountAmount}
                  hint="Optional: Cap the maximum discount amount for percentage discounts"
                >
                  <CurrencyInput
                    value={formData.maxDiscountAmount}
                    onChange={(value) => updateFormData('maxDiscountAmount', value)}
                    placeholder="Optional"
                    minValue={0}
                    error={errors.maxDiscountAmount}
                  />
                </FormField>
              )}
            </FormSection>

            {/* Validity Period */}
            <FormSection
              title="Validity Period"
              icon={<Calendar className="h-5 w-5" />}
              description="Set when this discount code is active"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Valid From"
                  required
                  error={errors.validFrom}
                >
                  <Input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => updateFormData('validFrom', e.target.value)}
                    className={errors.validFrom ? 'border-destructive' : ''}
                  />
                </FormField>

                <FormField
                  label="Valid To"
                  required
                  error={errors.validTo}
                >
                  <Input
                    type="date"
                    value={formData.validTo}
                    onChange={(e) => updateFormData('validTo', e.target.value)}
                    className={errors.validTo ? 'border-destructive' : ''}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Usage Limits */}
            <FormSection
              title="Usage Limits"
              icon={<Users className="h-5 w-5" />}
              description="Control how many times this discount can be used"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Total Usage Limit"
                  error={errors.usageLimit}
                  hint="Leave empty for unlimited usage"
                >
                  <NumberInput
                    value={formData.usageLimit}
                    onChange={(value) => updateFormData('usageLimit', value)}
                    placeholder="Unlimited"
                    minValue={0}
                    allowDecimals={false}
                    error={errors.usageLimit}
                  />
                </FormField>

                <FormField
                  label="Per User Limit"
                  required
                  error={errors.perUserLimit}
                  hint="How many times each user can use this code"
                >
                  <NumberInput
                    value={formData.perUserLimit}
                    onChange={(value) => updateFormData('perUserLimit', value)}
                    placeholder="1"
                    minValue={1}
                    allowDecimals={false}
                    error={errors.perUserLimit}
                  />
                </FormField>
              </FormGrid>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <label className="font-medium">First-time users only</label>
                    <p className="text-sm text-muted-foreground">
                      Restrict this discount to customers who haven't placed an order before
                    </p>
                  </div>
                  <Switch
                    checked={formData.firstTimeUsers}
                    onCheckedChange={(checked) => updateFormData('firstTimeUsers', checked)}
                  />
                </div>

                {discount && (
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <label className="font-medium">Active Status</label>
                      <p className="text-sm text-muted-foreground">
                        Control whether this discount code is currently active
                      </p>
                    </div>
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => updateFormData('isActive', checked)}
                    />
                  </div>
                )}
              </div>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {discount ? 'Update Discount' : 'Create Discount'}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
} 